'use client';

import {useEffect} from 'react';

type MultiselectFieldProps = {
  value: unknown;
  onChange: (value: string[]) => void;
  error?: string;
  disabled?: boolean;
  label: string;
  required?: boolean;
  options: string[];
};

export function MultiselectField({
  value,
  onChange,
  error,
  disabled,
  label,
  required,
  options,
}: MultiselectFieldProps) {
  // Defensive: a malformed staged Otto write (an LLM occasionally sends a
  // JSON-encoded string instead of a parsed list) could otherwise reach here.
  // Coerce non-arrays to [] for display, AND push the normalized value back
  // into form state — otherwise the malformed value keeps flowing to Save
  // even though the checkboxes render correctly.
  const isMalformed = !Array.isArray(value);
  const selected = isMalformed ? [] : (value as string[]);

  // Deliberately depends only on `isMalformed` — `onChange` isn't guaranteed
  // stable across renders, and re-running this on every identity change
  // would call it repeatedly while the field stays malformed.
  useEffect(() => {
    if (isMalformed && value !== undefined && value !== null) {
      onChange([]);
    }
  }, [isMalformed]);

  // Selected values that aren't valid options — e.g. a staged Otto write
  // proposing one that doesn't exist. Render them as removable rows so the
  // user can clear them, mirroring the Records MultiSelectField.
  const invalidSelected = selected.filter((item) => !options.includes(item));

  const handleToggle = (opt: string) => {
    if (selected.includes(opt)) {
      onChange(selected.filter((s) => s !== opt));
    } else {
      onChange([...selected, opt]);
    }
  };

  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      <div
        className={`rounded-md border p-2 ${error ? 'border-red-500' : 'border-gray-300'}`}
      >
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {options.map((opt) => (
            <label key={opt} className="flex items-center gap-2 py-1 text-sm">
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => {
                  handleToggle(opt);
                }}
                disabled={disabled}
                className="rounded border-gray-300 accent-primary-500"
              />
              {opt}
            </label>
          ))}
        </div>
        {options.length === 0 && (
          <p className="text-xs text-gray-400">No options configured</p>
        )}
        {invalidSelected.map((opt) => (
          <label
            key={`invalid-${opt}`}
            className="mt-1 flex items-center gap-2 rounded border border-error-300 bg-error-50 px-2 py-1 text-sm text-error-800"
          >
            <input
              type="checkbox"
              checked
              onChange={() => {
                handleToggle(opt);
              }}
              disabled={disabled}
              className="rounded border-gray-300 accent-error-500"
            />
            <span className="min-w-0 flex-1 truncate">{opt}</span>
            <span className="shrink-0 text-xs font-medium text-error-600">
              Not a valid option
            </span>
          </label>
        ))}
      </div>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}
